import { chain, find, get, isEmpty, toLower, some, includes, reduce, update, concat, pick, map, first, filter, keyBy, mapValues, cloneDeep, mergeWith, isObject, partition, orderBy, isNumber, isFunction, capitalize, findIndex, join, set, isFinite } from "lodash"
import { call, getContext, put } from "redux-saga/effects"
import { Creators as AnalyzeHeaderCreators } from "modules/analyze-header/model"
import { Creators as AnalyzeCompositionCreators } from "modules/analyze-composition/model"
import { Creators as ConcentrationDetailsCreators } from "modules/analyze-concentration-details/model"
import { Creators as PerformanceDetailsCreators } from "modules/analyze-performance-details/model"
import { Creators as RiskDetailsCreators } from "modules/analyze-risk-details/model"
import { Creators as BetaDetailsCreators } from "modules/analyze-beta-details/model"
import { Creators as AnalyzeExposureCreators } from "modules/analyze-historical-exposure/model"
import {
  FACTOR_VIEWS,
  METRICS,
  PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY,
  PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT,
} from "helpers/metrics/constants"
import { METRIC_TYPES, FACTOR_VIEW_OPTIONS, PERF_COLOR } from "helpers/assets/constants"
import { getBetaColumnsAndTooltips } from "helpers/tables"
import { getFactorShortLabel } from "../factors"
import { computeAddFilter, LONG_SHORT } from "../filters"
import { STRING_OPERATORS } from "helpers/filters/filter-ons/strings"
import { CLASSIFICATION_OPERATORS } from "../filters/filter-ons/classifications"
import { FORMATS, makeCustomFormatter, assetDollarFormatter } from "../formatters/numbers"
import { getGroup as getMenuGroup } from "helpers/menu"
import { buildContributorKey } from "../contributors"
import { CONTENT_SET_DATA_TYPES } from "../contentSetConstants"
import { titleCase } from "../string"
import { VIEW_TYPES } from "../base"
import { unwrapValue } from "./unwrapValue"
import { getLeverageTooltipComponent, getNMVTooltipComponent } from "./tooltips"
import { shouldAggregateColumn } from "helpers/factorSensitivity"

export const VIEW_QUERY_MAP = {
  risk: "riskFactors",
  performance: "performanceFactors",
  exposure: "exposure",
  sensitivity: "sensitivity",
  riskDecomposition: "riskFactors",
  beta: "beta",
}

const basicColumnGetter = (item, path) => get(item, path)

const shouldAggregateViewFactors = (selectedView, id) => includes([METRICS.RISK, METRICS.PERFORMANCE], selectedView) && id !== selectedView

export const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })
export const percentageFormatter = (number) => numberFormatter(number * 100)

export function* assetClickHandler({ screen, category, item, column, account, portfolio, currentGrouping, groups, segment, contentSetId }) {
  const adjustedScreen = contentSetId ? `contentset/${contentSetId}` : screen
  const { navigate } = yield getContext("router")
  yield call(navigate, `/${account}/p/${portfolio.shortId}/analyze/trends/${adjustedScreen}`)

  const { filters: existingFilters, normalization } = segment
  const grouping = item.isGroup ? currentGrouping : null
  const group = chain(groups).find(g => g.description === item.id).get("id").value()
  const filter = getFilter(item, currentGrouping, group)

  let combinedFilter = {
    ...(existingFilters ?? {}),
    ...(filter ?? {}),
  }
  if (isEmpty(combinedFilter)) {
    combinedFilter = null
  }

  yield put(AnalyzeHeaderCreators.setFilter(combinedFilter, false))

  if (normalization) {
    yield put(AnalyzeHeaderCreators.setNormalization(normalization))
  }

  switch (screen) {
    case "performance":
      yield put(PerformanceDetailsCreators.setContributorsGrouping(grouping))
      break
    case "composition":
      if (category === "concentration") {
        yield put(ConcentrationDetailsCreators.setContributorsGrouping(grouping))
      } else {
        yield put(BetaDetailsCreators.setContributorsGrouping(grouping))
      }
      yield put(AnalyzeCompositionCreators.setDisplayType(includes(["beta", "dynamicBeta"], column.query) ? "beta" : "concentration"))
      break
    case "risk":
      yield put(RiskDetailsCreators.setContributorsGrouping(grouping))
      break
    case "exposure":
      if (contentSetId) {
        yield put(AnalyzeExposureCreators.setDisplayType(category))
      }
      break
  }
}

export function getFilter(assetRow, grouping, group) {
  if (includes(["TOTAL", "SEGMENT"], assetRow.id)) {
    return null
  }
  let args, operator

  const isGroupOrDrillDown = assetRow.isGroup || assetRow.isDrillDown
  const isLongShort = isGroupOrDrillDown && grouping.id === "LONG_SHORT"
  const isClassification = isGroupOrDrillDown && grouping.isClassification
  const isDescriptorGroup = isGroupOrDrillDown && !isLongShort && !isClassification
  const isTagGroup = isGroupOrDrillDown && grouping.isTag
  const isAttributeGroup = isGroupOrDrillDown && grouping.isPositionAttribute

  switch (true) {
    case isLongShort:
      args = [LONG_SHORT, "string", group ?? assetRow.id, null, null]
      break
    case isClassification:
      operator = find(CLASSIFICATION_OPERATORS, ({ value }) => value === "eq")
      args = ["classification", "string", grouping.classificationId ?? grouping.id, operator, [{ value: group ?? assetRow.id, tierId: grouping.id }]]
      break
    case isTagGroup:
      operator = find(STRING_OPERATORS, ({ value }) => value === "eq")
      args = ["tag", "string", grouping.id, operator, [{ value: assetRow.id }]]
      break
    case isAttributeGroup:
      operator = find(STRING_OPERATORS, ({ value }) => value === "eq")
      args = ["positionAttributes", "string", grouping.id, operator, [{ value: assetRow.id }]]
      break
    case isDescriptorGroup:
      operator = find(STRING_OPERATORS, ({ value }) => value === "eq")
      args = ["descriptor", "string", toLower(grouping.id), operator, [{ value: group ?? assetRow.id }]]
      break
    default:
      operator = find(STRING_OPERATORS, ({ value }) => value === "in")
      args = ["securities", "string", assetRow.id, operator, [assetRow]]
      break
  }

  // Add common arguments non-used arguments before calling computeAddFilter
  // [existingFilter, ...args, contentSetId]
  return computeAddFilter({}, ...args, undefined)
}

export function getTracking(prefix) {
  const isGroupConditions = [
    (props) => get(props, "item.isGroup"),
    (props) => get(props, "item.isDrillDown"),
    (props) => get(props, "item.id") === "TOTAL",
  ]
  const getType = (props) => some(isGroupConditions, (fn) => fn(props)) ? "group" : "security"

  return {
    track: true,
    trackType: (props) => `${prefix}-${getType(props)}-analyze-link`,
    trackPayload: (props) => ({
      id: get(props, "item.label") ?? get(props, "item.id"),
      column: get(props, "column"),
    }),
  }
}

export const getAssetsExposureFieldName = (metricType, isGrouped) => {
  switch (metricType) {
    case METRIC_TYPES.NET_CONTRIBUTION:
      return "exposure: netContribution"

    case METRIC_TYPES.Z_SCORE:
      return isGrouped ? "" : "exposure: securityExposure"

    default:
      return "exposure: currencyExposure"
  }
}

export const getAssetsFactorSensitivityFieldName = (metricType, isGrouped) => {
  switch (metricType) {
    case METRIC_TYPES.NET_CONTRIBUTION:
      return "sensitivity"

    case METRIC_TYPES.STANDALONE:
      return isGrouped ? "" : "sensitivity: securityFactorSensitivity"

    default:
      return ""
  }
}

function isPercentView(view) {
  return includes(["risk", "sensitivity"], view)
}

function getFormatter(view, metric) {
  if (isPercentView(view)) { return percentageFormatter }
  if (metric === METRIC_TYPES.NET_CONTRIBUTION && view === "performance") { return percentageFormatter }
  if (includes([METRIC_TYPES.CURRENCY_EXPOSURE, METRIC_TYPES.CURRENCY_PERFORMANCE], metric)) { return assetDollarFormatter }
  return numberFormatter
}

function getOutputFormat(view) {
  if (isPercentView(view)) { return "percent" }
  return "number"
}

function getOutputFormatAndFormatter(view, metric) {
  const isPerformanceCurrencyMetric = metric === METRIC_TYPES.CURRENCY_PERFORMANCE
  let outputFormat = getOutputFormat(view)
  let formatter = getFormatter(view, metric)
  if (isPerformanceCurrencyMetric) {
    outputFormat = "number"
    formatter = numberFormatter
  }
  return { outputFormat, formatter }
}

function isFactorSensitivityView(selectedView) {
  return includes(selectedView, "factor-sensitivity")
}

function computeAdjustedSelectedView(selectedView, isContentSet) {
  if (isContentSet) {
    return "exposure"
  } else if (isFactorSensitivityView(selectedView)) {
    return "sensitivity"
  }
  return selectedView
}

function computeFactorsColumnsCategories(selectedView) {
  const category = chain(selectedView)
    .replace(/-/g, " ")
    .capitalize()
    .value()

  let categoryTooltip
  if (isFactorSensitivityView(selectedView)) {
    categoryTooltip = "1 STD Factor Impact (%)"
  }

  return {
    category,
    categoryTooltip,
  }
}

/* Group factor by categories and map them to columns
{
  category1: [factor1, factor2, ...],
  category2: [factor3, factor4, ...],
  ...
} */
export const computeFactorsColumns = (factorsMetadata, selectedView, metricType, isContentSet) => {
  const adjustedView = computeAdjustedSelectedView(selectedView, isContentSet)
  return reduce(factorsMetadata, (factorColumns, factor, factorId) =>
    update(factorColumns, factor.category, (existing) => concat(existing ?? [], {
      id: factorId,
      label: getFactorShortLabel(factorsMetadata, factorId),
      ...computeFactorsColumnsCategories(selectedView),
      path: `${VIEW_QUERY_MAP[adjustedView]}.${factorId}`,
      outputFormat: getOutputFormat(adjustedView),
      formatter: getFormatter(adjustedView, metricType),
      getter: (item, path) => get(item, path),
      allowAbsSort: true,
      query: VIEW_QUERY_MAP[adjustedView],
    })), {})
}

const getViewFromColumnQuery = (query) => {
  switch (query) {
    case "beta":
    case "dynamicBeta":
      return "composition"
    case "riskFactors":
    case "marginal":
      return "risk"
    case "performanceFactors":
      return "performance"
    default:
      return query
  }
}

// Add the makeClick prop to columns
// We return a function maker because we want some items to not be clickable, therefore not have an action
// We can't know the id before rendering so we defer the onClick making via makeClick to the UI
const addColumnMakeClick = (column, itemClickAction, selectedView) => {
  const makeItemClickAction = (view, subView, security, column, contentSetId) => {
    if (security.id === "Other") {
      return null
    }

    return (item) => {
      return itemClickAction(view, subView, item, column, contentSetId)
    }
  }

  let view = getViewFromColumnQuery(column.query)
  if (selectedView === "predicted-factor-sensitivity") {
    view = "factor-sensitivity/predicted"
  } else if (selectedView === "historical-factor-sensitivity") {
    view = "factor-sensitivity/historical"
  }

  return ({
    ...column,
    makeClick: (security) => makeItemClickAction(view, column.subView || "total", security, pick(column, ["id", "type", "query"]), column.contentSetId),
  })
}

export const mapColumnClickAndSort = (column, sort, subView, contentSetId, itemClickAction, selectedView) => {
  const columnInfo = {
    ...column,
    subView,
    contentSetId,
    ...(get(sort, "id") !== get(column, "id")
      ? { sorted: false }
      : {
        sorted: toLower(sort.direction),
        absSorted: sort.absolute,
      }),
  }

  if (!itemClickAction || column.disableClick) {
    return columnInfo
  }

  return addColumnMakeClick(columnInfo, itemClickAction, selectedView)
}

export function getColumnValue(security, column) {
  const { id, path } = column ?? {}
  const raw = get(security, path)
  const value = unwrapValue(raw)

  if (id === "percentEquity") {
    if (value == null) { return "" }
    return value / 100
  }
  return value
}

export function computeAssetViewMenuItems(isOpPro, contentSets, options = { includePerformance: false }) {
  const { includePerformance } = options
  return chain([
    {
      id: "summary",
      label: "Summary",
      ...getMenuGroup({ type: "model" }),
    }, (includePerformance && {
      id: "performance",
      label: "Performance",
      tooltip: "Ex-post performance with factor attribution",
      ...getMenuGroup({ type: "model" }),
    }),
    {
      id: "exposure",
      label: "Exposure",
      tooltip: "Weighted-average factor exposure",
      ...getMenuGroup({ type: "model" }),
    },
    {
      id: "beta",
      label: "Beta",
      tooltip: "The security's beta, evaluated as a standalone market instrument",
      ...getMenuGroup({ type: "model" }),
    },
    {
      id: "risk",
      label: "Risk",
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : "Ex-ante total risk & factor risk decomposition",
      ...getMenuGroup({ type: "model" }),
    },
    {
      id: "marginal",
      label: "Marginal Contribution",
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : "Evaluate the marginal impact on risk for any security",
      ...getMenuGroup({ type: "model" }),
    },
    {
      id: "predicted-factor-sensitivity",
      label: "Predicted Factor Sensitivity",
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using the risk model covariance matrix.",
      ...getMenuGroup({ type: "factorSensitivity" }),
    },
    {
      id: "historical-factor-sensitivity",
      label: "Historical Factor Sensitivity",
      tooltip: "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using historical realized factor returns.",
      ...getMenuGroup({ type: "factorSensitivity" }),
    },
    ...map(contentSets, ({ id, name, description, proprietary }) => ({
      id,
      label: name,
      tooltip: description,
      ...getMenuGroup({ type: "contentset", proprietary }),
    })),
  ])
    .compact()
    .map((item, i) => ({ ...item, order: i }))
    .value()
}

function mapContentSetCategoriesToMenuItems(contentSetCategories) {
  return map(contentSetCategories, ({ id, name, description }) => ({
    id,
    primaryText: name,
    ...(description && { secondaryText: description }),
  }))
}

const COLUMN_ORDER_OPTIONS = [{
  id: "default",
  primaryText: "Default",
  secondaryText: "The view’s default ordering",
}, {
  id: "magnitude",
  primaryText: "Biggest Contributor",
  secondaryText: "Ordered high to low by contribution to this instrument",
}]

export function computeColumnOrderOptions(selectedView) {
  if (includes(["summary", "beta", "marginal"], selectedView)) {
    return null
  }
  return COLUMN_ORDER_OPTIONS
}

export function computeSubViewOptions(selectedView, contentSetCategories, modelFactorCategories, selectedViewIsContentSet) {
  if (!isEmpty(contentSetCategories)) {
    return mapContentSetCategoriesToMenuItems(contentSetCategories)
  }

  if ((selectedViewIsContentSet && isEmpty(contentSetCategories)) || includes(["summary", "marginal", "beta"], selectedView)) {
    return
  }

  if (isEmpty(modelFactorCategories)) {
    return FACTOR_VIEW_OPTIONS
  }

  const mappedCategories = chain(modelFactorCategories)
    .orderBy("singleFactor", "asc")
    .map(({ id, label }) => ({ id, primaryText: label }))
    .value()

  if (shouldAggregateViewFactors(selectedView)) {
    return [
      { id: FACTOR_VIEWS.ALL, primaryText: "Factors" },
      ...mappedCategories,
    ]
  }

  return mappedCategories
}

export function computeSelectedSubView(selectedSubView, subViewOptions) {
  if (selectedSubView && some(subViewOptions, option => option.id === selectedSubView)) {
    return selectedSubView
  }
  return get(first(subViewOptions), "id")
}

export function computeSelectedMetricType(selectedMetricType, metricTypeOptions) {
  const allowedOptions = filter(metricTypeOptions, ({ disabled }) => !disabled)
  const defaultOptionId = get(find(allowedOptions, "isDefault"), "id")
  return selectedMetricType || defaultOptionId || get(first(allowedOptions), "id")
}

export function computeContentSetFactorsFromCategories(selectedSubView, categories) {
  return get(categories, [selectedSubView, "factors"])
}

function getViewDefaultValue(view, isSensitivityContentSet = false) {
  if (view === METRICS.EXPOSURE && isSensitivityContentSet) {
    return METRIC_TYPES.CURRENCY_EXPOSURE
  }

  if (view === METRICS.BETA) {
    return METRIC_TYPES.STANDALONE
  }

  return METRIC_TYPES.NET_CONTRIBUTION
}

export function computeMetricTypeOptions(baseOptions, selectedView, isContentSet, assetType, selectedContentSetDataType, defaultMetricUnits) {
  const isSensitivityContentSet = selectedContentSetDataType === CONTENT_SET_DATA_TYPES.SENSITIVITY
  const adjustedView = computeAdjustedSelectedView(selectedView, isContentSet)
  const defaultValue = getViewDefaultValue(adjustedView, isSensitivityContentSet)
  const options = chain(baseOptions)
    .get(adjustedView)
    .map(option => ({
      ...option,
      isDefault: option.id === get(defaultMetricUnits, adjustedView, defaultValue),
    }))
    .value()

  const isCompositeAssetType = !isContentSet && includes(["ETF", "INDEX"], assetType)
  if (adjustedView === "exposure" && (isCompositeAssetType || (isContentSet && !isSensitivityContentSet))) {
    return update(options, [findIndex(options, { id: METRIC_TYPES.CURRENCY_EXPOSURE })], metricOption => ({
      ...metricOption,
      disabled: true,
      tooltip: isCompositeAssetType ? "Composite securities do not support currency exposure" : "This content set does not support currency exposure",
    }))
  }
  return !isEmpty(options) ? options : undefined
}

export function computeSort(sort) {
  return get(sort, "id") === "id" ? sort.direction : null
}

export function applySort(data, sort, columns, idColumnPath = "id") {
  const direction = get(sort, "direction")
  const absSort = get(sort, "absolute", false)

  let sortedColumnPath = idColumnPath
  if (sort.id !== "id") {
    sortedColumnPath = chain(columns)
      .find(column => column.id === sort.id)
      .get("path")
      .value()
  }

  let [positions, missingValuePositions] = partition(data, item => {
    const value = get(item, sortedColumnPath)
    return !!value || value === 0
  })

  const order = [pos => {
    const value = get(pos, sortedColumnPath)
    // This accounts for residual attribution, which is an object
    const sortValue = value?.value ?? value
    return absSort && isNumber(sortValue) ? Math.abs(sortValue) : sortValue
  }]
  const dir = [toLower(direction)]

  // Resolve ties
  if (sort.id !== "id") {
    order.push(idColumnPath)
    dir.push("asc")
  }

  positions = orderBy(positions, order, dir)
  missingValuePositions = orderBy(missingValuePositions, idColumnPath, "asc")

  return concat(positions, missingValuePositions)
}

export function makeComputeAssetAnalyticsColumns(itemClick) {
  return (viewColumnsMap, sort, selectedView, selectedSubView, factorColumns, isOpPro, disableColumnClick, selectedViewIsContentSet, categories, metricType) => {
    const adjustedView = computeAdjustedSelectedView(selectedView, selectedViewIsContentSet)

    const columns = chain(viewColumnsMap)
      .get(adjustedView)
      .thru((columns) => {
        if (shouldIncludeFactorColumns(adjustedView)) {
          return concat(columns, get(factorColumns, selectedSubView, []))
        }
        return columns
      })
      .filter(column => !isOpPro || get(column, "opPro") !== false)
      .map(column => {
        const contentSetId = selectedViewIsContentSet ? selectedView : null
        const itemClickAction = disableColumnClick ? null : itemClick
        return mapColumnClickAndSort(column, sort, selectedSubView, contentSetId, itemClickAction, selectedView)
      })
      .value()

    return getAggregatedColumnsWithCategories(columns, categories, selectedView, metricType, selectedSubView)
  }
}

function shouldIncludeFactorColumns(view) {
  return includes(["exposure", "risk", "sensitivity", "performance"], view)
}

export function getGroupingLabel(grouping, isActive) {
  if (get(grouping, "id") !== "LONG_SHORT") {
    return get(grouping, "label")
  }
  return isActive ? "Overweight / Underweight" : "Long / Short"
}

export function contributorsReducer(acc, contributor) {
  const key = buildContributorKey(contributor)
  if (acc[key]) {
    // The reducer is used to de-duplicate the contributors, but the economic exposure needs to be summed
    acc[key].economicExposure += contributor.economicExposure
  } else {
    acc[key] = cloneDeep(contributor)
  }
  return acc
}

export function isCashOffsetContributor(contributor) {
  return get(contributor, "modelProviderId") === "CASH_OFFSET"
}

export function computeGroupsMap(groups, { mapFn, exposureMap }) {
  const groupsById = keyBy(groups, "id")

  if (exposureMap) {
    return mapValues(groupsById, group => chain(group)
      .get("contributors")
      .reduce(contributorsReducer, {})
      .mapValues(contributor => mapFn(contributor, exposureMap[group.id]))
      .value())
  }

  return mapValues(groupsById, group => chain(group)
    .get("contributors")
    .keyBy(buildContributorKey)
    .mapValues(mapFn)
    .value())
}

// When merging data maps [composition, performance, risk, beta, ...]
// we can't simply use merge as that will override the notHeld value which can be different between performance and others
// In the case of notHeld, if any versions of it is false, we consider it to be false. It means it was held at some point in time
function customizer(accumulator, incomingValue, objectProperty) {
  if (!isObject(incomingValue)) {
    // The main purpose of having the customizer is to be able to prevent the notHeld property changing from false to true
    // Whenever one of the maps has a notHeld property set to false, we want to keep it that way
    if (objectProperty === "notHeld") {
      return accumulator && incomingValue
    }
    return incomingValue
  }
  return mergeWith(accumulator, incomingValue, customizer)
}

// Data map: { securityId: { ...metric } }
// eg. [
// { "QQWVZ5R20": { ...descriptors, notHeld, economicExposure, ... }, // COMPOSITION
// { "QQWVZ5R20": { "risk": { "total": -0.00004008985, "factors": -0.0000437114, "specific": 0.00000362155 } }, // RISK
// ...]
export function mergeContributorsMetrics(...dataMaps) {
  return mergeWith({}, ...dataMaps, customizer)
}

function makePerformanceColumn(id, label, path, metricType, { hideWhenNoData = false, downloadOnly = false } = {}) {
  const isPerformanceCurrencyMetric = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE
  return {
    id,
    category: isPerformanceCurrencyMetric
      ? PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY
      : PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT,
    fixedColumnOrder: true,
    path,
    label,
    allowAbsSort: true,
    outputFormat: isPerformanceCurrencyMetric ? "number" : "percent",
    formatter: isPerformanceCurrencyMetric ? assetDollarFormatter : percentageFormatter,
    getter: basicColumnGetter,
    query: "performance",
    color: PERF_COLOR,
    hideWhenNoData,
    downloadOnly,
  }
}

export function computeColumns(gmv, referenceEquity, indexLeverage, selectedMetricType = METRIC_TYPES.NET_CONTRIBUTION, modelName, betaInstrument, selectedPerformanceMetricType, isRfrAttributionEnabled) {
  const { historical, predicted, custom } = getBetaColumnsAndTooltips(modelName, betaInstrument) || {}
  const isStandaloneMetric = selectedMetricType === METRIC_TYPES.STANDALONE
  const canAggregate = !isStandaloneMetric
  const betaColumnPath = (fragment) => {
    const pathFragments = ["beta"]
    if (isStandaloneMetric) {
      pathFragments.push("standalone")
    }
    pathFragments.push(fragment)
    return join(pathFragments, ".")
  }

  return {
    composition: [{
      id: "economicExposure",
      path: "composition.economicExposure",
      label: "NMV",
      outputFormat: "number",
      formatter: assetDollarFormatter,
      getter: basicColumnGetter,
      allowAbsSort: true,
      query: "composition",
      isSticky: true,
      tooltipContents: getNMVTooltipComponent(gmv, referenceEquity),
    }, {
      id: "percentEquity",
      path: "composition.percentEquity",
      label: (isActive) => isActive ? "Active % Equity" : "% Equity",
      outputFormat: "percent",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      allowAbsSort: true,
      query: "composition",
      isSticky: true,
      tooltipContents: getLeverageTooltipComponent(indexLeverage),
    }],
    performance: [
      makePerformanceColumn("totalPerformance", "Total", "performance.total", selectedPerformanceMetricType),
      makePerformanceColumn("performanceAlpha", "Alpha", "performance.specific", selectedPerformanceMetricType),
      makePerformanceColumn("performanceFactors", "Factors", "performance.factors", selectedPerformanceMetricType),
      makePerformanceColumn("performanceTrading", "Trading", "performance.trading", selectedPerformanceMetricType, { hideWhenNoData: true }),
      ...isRfrAttributionEnabled ? [makePerformanceColumn("performanceResidual", "Residual", "performance.residual", selectedPerformanceMetricType, { downloadOnly: true })] : [],
    ],
    risk: [{
      id: "totalRisk",
      category: "% Predicted Risk Contribution",
      fixedColumnOrder: true,
      path: "risk.total",
      label: "Total",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "risk",
      opPro: false,
    }, {
      id: "specificRisk",
      category: "% Predicted Risk Contribution",
      fixedColumnOrder: true,
      path: "risk.specific",
      label: "Specific",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "risk",
      opPro: false,
    }, {
      id: "riskFactors",
      category: "% Predicted Risk Contribution",
      fixedColumnOrder: true,
      path: "risk.factors",
      label: "Factors",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "risk",
      opPro: false,
    }],
    marginalContribution: [{
      id: "totalRisk",
      category: "Total Risk",
      fixedColumnOrder: true,
      path: "risk.total",
      label: (isActive) => isActive ? "Total % of Active Risk" : "Total % of Risk",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
    }, {
      id: "totalRiskContribution",
      category: "Total Risk",
      fixedColumnOrder: true,
      path: "marginal.total.riskContribution",
      label: (isActive) => isActive ? "Active Risk Contribution" : "Risk Contribution",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Total",
    }, {
      id: "totalRiskMarginalContribution",
      category: "Total Risk",
      fixedColumnOrder: true,
      path: "marginal.total.marginalContribution",
      label: "Marginal Contribution",
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Total",
    }, {
      id: "totalRiskCorrelation",
      category: "Total Risk",
      fixedColumnOrder: true,
      path: "marginal.total.correlation",
      label: (isActive) => isActive ? "Relative Correlation" : "Correlation",
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
    }, {
      id: "totalRiskVolatility",
      category: "Total Risk",
      fixedColumnOrder: true,
      path: "marginal.total.volatility",
      label: (isActive) => isActive ? "Relative Volatility" : "Volatility",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
    }, {
      id: "specificRisk",
      category: "Specific Risk",
      fixedColumnOrder: true,
      path: "risk.specific",
      label: (isActive) => isActive ? "Specific % of Active Risk" : "Specific % of Risk",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "risk",
      opPro: false,
    }, {
      id: "specificRiskContribution",
      category: "Specific Risk",
      fixedColumnOrder: true,
      path: "marginal.specific.riskContribution",
      label: (isActive) => isActive ? "Active Risk Contribution" : "Risk Contribution",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Specific",
    }, {
      id: "specificRiskMarginalContribution",
      category: "Specific Risk",
      fixedColumnOrder: true,
      path: "marginal.specific.marginalContribution",
      label: "Marginal Contribution",
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Specific",
    }, {
      id: "factorsRisk",
      category: "Factors Risk",
      fixedColumnOrder: true,
      path: "risk.factors",
      label: (isActive) => isActive ? "Factors % of Active Risk" : "Factors % of Risk",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "risk",
      opPro: false,
    }, {
      id: "factorsRiskContribution",
      category: "Factors Risk",
      fixedColumnOrder: true,
      path: "marginal.factors.riskContribution",
      label: (isActive) => isActive ? "Active Risk Contribution" : "Risk Contribution",
      allowAbsSort: true,
      outputFormat: "percent",
      formatter: percentageFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Factors",
    }, {
      id: "factorsRiskMarginalContribution",
      category: "Factors Risk",
      fixedColumnOrder: true,
      path: "marginal.factors.marginalContribution",
      label: "Marginal Contribution",
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      query: "marginal",
      opPro: false,
      canAggregate: false,
      disableClick: true,
      downloadLabelPrefix: "Factors",
    }],
    beta: [{
      id: "predictedBeta",
      category: capitalize(get(predicted, "category")),
      fixedColumnOrder: true,
      path: betaColumnPath("predicted"),
      canAggregate,
      label: get(predicted, "label"),
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      tooltipContents: get(predicted, "tooltip"),
      query: "beta",
    },
    {
      id: "historicalBeta",
      category: capitalize(get(historical, "category")),
      fixedColumnOrder: true,
      path: betaColumnPath("historical"),
      canAggregate,
      label: get(historical, "label"),
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      tooltipContents: get(historical, "tooltip"),
      query: "beta",
    }],
    dynamicPredictedBeta: {
      id: "dynamicPredictedBeta",
      category: capitalize(get(custom, "category")),
      fixedColumnOrder: true,
      path: betaColumnPath("dynamicPredicted"),
      canAggregate,
      label: get(custom, "label"),
      allowAbsSort: true,
      outputFormat: "number",
      formatter: numberFormatter,
      getter: basicColumnGetter,
      tooltipContents: get(custom, "tooltip"),
      query: "dynamicBeta",
    },
  }
}

export function computeFileName(assetName, grouping, selectedGroupName, viewType, endDate, selectedView, metricType, selectedSubView, metricTypeOptions, { dataType, isIntraday = false }) {
  let fileName = assetName
  if (isIntraday) {
    fileName = `Intraday ${fileName}`
  }

  // Add group by or selected group
  const groupingLabel = get(grouping, "label", "")
  if (groupingLabel) {
    fileName = selectedGroupName
      ? `${fileName} - Total ${selectedGroupName}`
      : `${fileName} - Total Grouped By ${groupingLabel}`
  }

  // Add active/absolute
  if (!isIntraday) {
    fileName = `${fileName} - ${viewType === VIEW_TYPES.ACTIVE ? "Active" : "Absolute"} ${dataType}`
  }

  if (toLower(selectedView) === "marginal") {
    fileName = `${fileName} - Marginal Contribution`
  } else if (toLower(selectedView) !== "summary") {
    const metricTypeLabel = get(find(metricTypeOptions, ({ id }) => id === metricType), "primaryText", "")
    const selectedSubViewLabel = selectedSubView === FACTOR_VIEWS.ALL ? "All Factors" : capitalize(selectedSubView)
    fileName = `${fileName} - ${capitalize(selectedView)} - ${metricTypeLabel} - ${selectedSubViewLabel}`
  }

  // Add asOf date
  fileName = `${fileName} ${isIntraday ? "as of" : "-"} ${endDate}`

  return fileName
}

export function computeColumnLabel({ label: columnLabel, category, downloadLabelPrefix }, selectedView, isActive = false) {
  const label = isFunction(columnLabel) ? columnLabel(isActive) : columnLabel
  if (toLower(selectedView) === "summary" && category) {
    return `${titleCase(category)} ${label}`
  }
  if (downloadLabelPrefix) {
    return `${titleCase(downloadLabelPrefix)} ${label}`
  }
  return label
}

export function computeIdColumnPath(groupingOptions, selectedGroup) {
  return groupingOptions && !selectedGroup ? "description" : "id"
}

export function betaColumnFilter(beta, missingDataFiles) {
  return filter(beta, ({ id }) => !includes(missingDataFiles, id))
}

function getAggregableCategories(categories, selectedFactorView, selectedSubView) {
  return filter(categories, ({ id, isSelectedView }) => (
    id !== selectedFactorView
    && includes([FACTOR_VIEWS.ALL, id], selectedSubView)
    && !isSelectedView
  ))
}

export function getAggregatedColumnsWithCategories(columns, categories, selectedView, metricType, selectedSubView) {
  if (!shouldAggregateViewFactors(selectedView)) {
    return columns
  }
  const query = VIEW_QUERY_MAP[selectedView]
  const aggregableCategories = getAggregableCategories(categories, selectedView, selectedSubView)
  const { outputFormat, formatter } = getOutputFormatAndFormatter(selectedView, metricType)
  return [
    ...columns,
    ...map(aggregableCategories, category => ({
      ...category,
      category: FACTOR_VIEWS.ALL,
      path: `${query}.${category.id}`,
      query,
      formatter,
      outputFormat,
      getter: (contributor, path) => get(contributor, path),
      canAggregate: true,
    }))]
}

export function getFactorCategoryTotalColumn(column, category, selectedView) {
  if (!shouldAggregateViewFactors(selectedView, column.id)) {
    return null
  }

  if (column.query !== VIEW_QUERY_MAP[selectedView]) {
    return null
  }

  if (column.category && category && !category.singleFactor) {
    return {
      columns: [{
        id: category.id,
        label: category.label,
        category: `${column.category}Total`,
        formatter: column.formatter,
        outputFormat: getOutputFormat(selectedView),
        path: `${column.query}.${category.id}`,
        getter: (contributor, path) => get(contributor, path),
      }],
    }
  }
  return null
}

export function shouldFilterToColumnGroup(column, selectedView, selectedSubView) {
  if (!shouldAggregateViewFactors(selectedView, column.id)) {
    return false
  }
  if (selectedSubView && selectedSubView !== FACTOR_VIEWS.ALL) {
    return false
  }

  if (column.query === VIEW_QUERY_MAP[selectedView]) {
    return true
  }
  return false
}

export function getFactorsFromContributor(contributor, valueGetter) {
  const factors = reduce(get(contributor, "attribution.factors"), (acc, factor) => {
    set(acc, factor.id, valueGetter(factor))
    update(acc, factor.category, (currentValue) => (currentValue || 0) + valueGetter(factor))
    return acc
  }, {})
  return factors
}

export const extractContributorRiskFactor = (contributor) => ({
  ...pick(contributor, ["id", "assetClass", "modelProviderId"]),
  riskFactors: getFactorsFromContributor(contributor, (factor) => factor.value),
})

const computeContributorColumnTotals = (columnTotals, columns, contributor, metricType) => reduce(
  columns,
  (columnTotals, { path, query, canAggregate }) => {
    if (shouldAggregateColumn(query, metricType, canAggregate)) {
      const value = get(contributor, path)
      if (path) {
        if (isObject(value) && isFinite(value.value)) {
          update(columnTotals, path, (currentValue) => (currentValue || 0) + value.value)
        } else if (isFinite(value)) {
          update(columnTotals, path, (currentValue) => (currentValue || 0) + value)
        }
      }
    }
    return columnTotals
  },
  columnTotals)

export const extractContributorComposition = (contributor, economicExposureMap) => {
  const composition = {
    economicExposure: get(economicExposureMap, buildContributorKey(contributor)),
    percentEquity: contributor.economicExposure,
  }
  return {
    id: !isEmpty(contributor.id) ? contributor.id : "Unknown",
    ...contributor,
    ...(isFinite(contributor.economicExposure) && {
      ...composition,
      composition,
    }),
  }
}

export const computeAggregatedColumnTotals = (label, contributors, columns, metricType) => {
  return reduce(contributors,
    (columnTotals, contributor) => computeContributorColumnTotals(columnTotals, columns, contributor, metricType),
    {
      id: label,
      isAggregate: true,
    })
}

export const getAssetsReviewColumnsByCategories = (columns, data, selectedSubView, selectedView, selectedMetricType, categories, invalidBetaInstrumentReason, selectedViewIsContentSet = false) => {
  const category = find(categories, ({ id }) => id === selectedSubView)

  const mappedColumns = chain(columns)
    .filter(({ hideWhenNoData, path, id, downloadOnly }) => !downloadOnly && (selectedViewIsContentSet || id !== selectedSubView) && (!hideWhenNoData || some(data, d => get(d, path))))
    .reduce((current, rawColumn) => {
      const column = (rawColumn.id === "dynamicPredictedBeta" && invalidBetaInstrumentReason)
        ? { ...rawColumn, adjustedReason: invalidBetaInstrumentReason }
        : rawColumn
      const categoryColumns = get(current, [column.category, "columns"], [])
      const factorCategoryTotalColumn = getFactorCategoryTotalColumn(column, category, selectedView)
      if (factorCategoryTotalColumn) {
        current[`${column.category}Total`] = factorCategoryTotalColumn
      }
      if (shouldFilterToColumnGroup(column, selectedView, selectedSubView)) {
        const categoryGroup = VIEW_QUERY_MAP[selectedView]
        const categoryGroupColumns = get(current, [categoryGroup, "columns"], [])
        current[categoryGroup] = {
          ...get(current, [categoryGroup], {}),
          category: `${capitalize(selectedView)} Factors`,
          query: categoryGroup,
          fixedColumnOrder: false,
          columns: [...categoryGroupColumns, {
            ...column,
            path: `${categoryGroup}.${column.id}`,
            formatter: getFormatter(selectedView, selectedMetricType),
            outputFormat: getOutputFormat(selectedView),
            getter: basicColumnGetter,
            canAggregate: true,
          }],
        }
      } else {
        const { outputFormat, formatter } = getOutputFormatAndFormatter(selectedView, selectedMetricType)
        current[column.category] = {
          category: (!selectedViewIsContentSet && column.id === selectedSubView) ? FACTOR_VIEWS.ALL : `${get(column, "category", "")}`,
          categoryTooltip: column.categoryTooltip,
          color: column.color,
          columns: [...categoryColumns, column],
          fixedColumnOrder: column.fixedColumnOrder,
          outputFormat,
          formatter,
          getter: basicColumnGetter,
        }
      }
      return current
    }, {})
    .values()
    .value()

  return mappedColumns
}
